import { Button } from "@albertine/design-system/components/Button";
import { useAlbertineTranslation } from "albertine-shared-web";
import { Loader2, Share } from "lucide-react";
import type { ReactNode } from "react";
import { useShareSpinner } from "@/routes/-hooks/useShareSpinner";
import { useScrollIsOnTop } from "@/shared/hooks/useScrollIsOnTop";

type ShareButtonProps = {
    onShare: () => void;
    isPending?: boolean;
    isDisabled?: boolean;
    placement: "overlay" | "sticky";
    textColorClass?: string;
};

export function ShareButton({
    onShare,
    isPending = false,
    isDisabled = false,
    placement,
    textColorClass,
}: ShareButtonProps) {
    const t = useAlbertineTranslation();
    const label = t("share");
    const { showSpinner, startSpinner } = useShareSpinner(isPending);

    const colorClass =
        textColorClass ??
        (placement === "overlay"
            ? "text-components-booking-hero-text!"
            : "text-components-button-text-default-text hover:text-components-button-text-hover-text active:text-components-button-text-pressed-text");

    function handleShare() {
        if (showSpinner) {
            return;
        }

        startSpinner();
        onShare();
    }

    const button = (
        <Button
            variant="text"
            onPress={handleShare}
            aria-busy={showSpinner}
            isDisabled={isPending || isDisabled}
            truncate={false}
            className={`px-16 py-12 ${colorClass}`}
            aria-label={label}
        >
            {showSpinner ? (
                <Loader2
                    className="mr-4 size-20 shrink-0 animate-spin text-current motion-reduce:animate-none"
                    aria-hidden="true"
                />
            ) : (
                <Share className="mr-4 size-20 shrink-0" />
            )}
        </Button>
    );

    if (placement === "sticky") {
        return <div className="absolute right-0 z-10">{button}</div>;
    }

    return <ShareButtonOverlay>{button}</ShareButtonOverlay>;
}

function ShareButtonOverlay({ children }: { children: ReactNode }) {
    const onTop = useScrollIsOnTop();

    return (
        <div
            className={`absolute top-0 right-0 z-10 pt-(--twsa-safe-area-inset-top) transition-all duration-200 ease-in-out ${
                onTop
                    ? "visible translate-y-0 opacity-100"
                    : "pointer-events-none invisible -translate-y-16 opacity-0"
            }`}
        >
            {children}
        </div>
    );
}
